// @ts-ignore
import cookies from 'vue-cookies';
import { i18nScope, t, tIgnore } from '@/languages';
import { messageMap } from '@/languages/messageMap';
import { systemSessionEnum } from '@/types/chat/msg-item';

type LangType = 'zh-CN' | 'zh-TW' | 'zh-HK' | 'en';

type TinymceLangType = 'zh_CN' | 'zh-Hant' | 'en';

type I18nType = 'zh' | 'en' | 'cht';

type I18nHeaderType = 'zh' | 'en' | 'tw';

let locale: I18nType = 'zh';

type I18nData = {
  l: LangType;
  i18nHeader: I18nHeaderType;
  tinymce: TinymceLangType;
  calendar: 'zh-cn' | 'zh-hk' | 'en';
};

const DEFAULT_LANGUAGE: I18nType = 'zh';

// element 语言包
const ElementLocaleList: Record<I18nType, Promise<any>> = {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  en: () => import('element-ui/lib/locale/lang/en'),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  cht: () => import('element-ui/lib/locale/lang/zh-TW'),
};

// calendar 语言包
const CalendarLocaleList: Record<I18nType, Promise<any>> = {
  // @ts-ignore
  zh: () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment,import/extensions,import/no-unresolved
    return import(
      // @ts-ignore
      'vue-full-calendar/node_modules/fullcalendar/dist/locale/zh-cn'
    );
  },
  // @ts-ignore
  cht: () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment,import/extensions,import/no-unresolved
    return import(
      // @ts-ignore
      'vue-full-calendar/node_modules/fullcalendar/dist/locale/zh-hk'
    );
  },
};

// moment.js 语言包
const MomentLocaleList: Record<I18nType, Promise<any>> = {
  // @ts-ignore
  zh: () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment,import/extensions,import/no-unresolved
    return import(
      // @ts-ignore
      'moment/dist/locale/zh-cn'
    );
  },
  // @ts-ignore
  cht: () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment,import/extensions,import/no-unresolved
    return import(
      // @ts-ignore
      'moment/dist/locale/zh-hk'
    );
  },
};

const i18nData = new Map<I18nType, I18nData>([
  [
    'zh',
    {
      l: 'zh-CN',
      i18nHeader: 'zh',
      tinymce: 'zh_CN',
      calendar: 'zh-cn',
    },
  ],
  [
    'en',
    {
      l: 'en',
      i18nHeader: 'en',
      tinymce: 'en',
      calendar: 'en',
    },
  ],
  [
    'cht',
    {
      l: 'zh-HK',
      i18nHeader: 'tw',
      tinymce: 'zh-Hant',
      calendar: 'zh-hk',
    },
  ],
]);

export function getCalendarLanguage(): string {
  return (i18nData.get(locale) || i18nData.get(DEFAULT_LANGUAGE)!)
    .calendar as string;
}

export function getTinymceLanguage(): string {
  return (i18nData.get(locale) || i18nData.get(DEFAULT_LANGUAGE)!)
    .tinymce as string;
}

export function getCurrentLang(): I18nType {
  return locale;
}

export function getCurrentHeaderLang() {
  return (i18nData.get(locale) || i18nData.get(DEFAULT_LANGUAGE)!).i18nHeader;
}

export function setI18nLanguage(lang: I18nType, isReload = true): I18nType {
  locale = lang;
  localStorage.setItem('lang', locale);
  (cookies as any).set('l', locale, '3y', '/');

  if (isReload) {
    window.location.reload();
  }
  return lang;
}

function getSystemLanguage(): I18nType {
  // PC端默认中文
  if (IS_PC) {
    return DEFAULT_LANGUAGE;
  }
  const lang = window.navigator.language as LangType;

  if (/^en.*/gi.test(lang)) {
    return 'en';
  }
  if (['zh-HK', 'zh-TW'].includes(lang)) {
    return 'cht';
  }
  return 'zh';
}

async function getDefaultLanguage(): Promise<I18nType> {
  if (IS_ELECTRON) {
    const l = await window.electron.ipcRenderer.invoke('get-language');
    return setI18nLanguage(l, false);
  }

  // 优先取cookie
  // 判断cookie获取到的 是否在支持列表  支持的话直接使用
  // 不支持则获取浏览器语言  判断是否在支持列表  支持的话直接使用
  // 不支持则默认中文
  const cacheLang = (cookies as any).get('l') as I18nType;

  if (i18nData.has(cacheLang)) {
    return setI18nLanguage(cacheLang, false);
  }

  const systemLanguage = getSystemLanguage();

  if (systemLanguage) {
    return setI18nLanguage(systemLanguage, false);
  }

  return setI18nLanguage(DEFAULT_LANGUAGE, false);
}

async function getMomentPack(l: I18nType) {
  if (l !== 'en') {
    try {
      await (MomentLocaleList as any)[l]();
      // eslint-disable-next-line no-underscore-dangle
      // moment.locale(pack.default._abbr, pack.default);
    } catch (e) {
      console.error('moment.js加载语言包失败', e);
    }
  }
}

async function getCalendarPack(l: I18nType) {
  if (l !== 'en') {
    try {
      await (CalendarLocaleList as any)[l]();
    } catch (e) {
      console.error('calendar加载语言包失败', e);
    }
  }
}

async function getElementUIPack(l: I18nType) {
  if (l !== DEFAULT_LANGUAGE) {
    try {
      const elementPack = await (ElementLocaleList as any)[l]();

      // CDN加载element-ui  需要从window中locale中切换语言包
      await (window as any).ELEMENT.locale(elementPack.default);
    } catch (e) {
      console.error('element-ui加载语言包失败', e);
    }
  }
}

export async function changeI18nLanguage() {
  locale = await getDefaultLanguage();

  document.querySelector('html')?.setAttribute('lang', i18nData.get(locale)!.l);

  await i18nScope.change(locale as string);
  await getElementUIPack(locale);
  await getMomentPack(locale);
  await getCalendarPack(locale);

  return locale;
}
const matchReg = /\{(\s*.*?\s*)\}/g;
const splitReg = /(\{\s*.*?\s*\})/g;
/**
 * 使用消息模版code、params  转换为分割数组
 */
export function translateMessage(value: any, contextList: any) {
  const code = value.langCode;
  const params = JSON.parse(value.langParams);

  if (!code || !params) {
    return [];
  }
  // @ts-ignore
  const msgData = messageMap[code];

  if (!msgData) {
    return [];
  }

  const msg = msgData[locale];

  if (!msg) {
    return [];
  }

  const matchs = {};
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = matchReg.exec(msg)) !== null) {
    // @ts-ignore
    // eslint-disable-next-line prefer-destructuring
    matchs[match[0]] = match[1];
  }

  return msg.split(splitReg).map((tick: any) => {
    // @ts-ignore
    const matchData = matchs[tick];
    if (matchData) {
      const matchParams = params[matchData];
      return {
        text: matchParams.name,
        type: matchParams.type,
        businessId: matchParams.jumpId,
      };
    }
    return {
      text: tick,
      type: systemSessionEnum.text,
      businessId: null,
    };
  });
}

/**
 * 使用消息模版code、params
 */
export function translateOtherMessage(
  ext: any,
  msgItem: any,
  msgType?: string
) {
  const { langCode } = ext;
  const params = ext.params || ext.langParams;

  if (!langCode) {
    return { ...ext };
  }
  // @ts-ignore
  const msgData = messageMap[langCode];

  if (!msgData) {
    return { ...ext };
  }
  const msg = msgData[locale];
  if (!msg) {
    return [];
  }
  if (msgType === 'task') {
    const val = msg.split('【');
    return { desc: val[1].split('】')[1] };
  }

  let val = msg;
  if (!params) {
    // 不需要替换的  直接找到模板返回模板
    return { ...msgItem, text: msg };
  }

  let paramsObj = params;
  if (typeof params === 'string') {
    paramsObj = JSON.parse(params);
  }

  const keyList = Object.keys(paramsObj);
  if (keyList.length) {
    keyList.forEach((k) => {
      const reg = `{${k}}`;
      val = val.replace(reg, paramsObj[k]?.name || paramsObj[k]);
    });

    return { ...msgItem, text: val };
  }
}

export function setParameterOrder(list: any) {
  if (locale === 'en') {
    return list.reverse();
  }
  return list;
}

// 日期分隔符，全局替换
export function getDataSeparator() {
  let separator = t('至');
  if (locale === 'en') {
    separator = tIgnore('~');
  }
  return separator;
}
